import React from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Avatar,
  Chip,
  Stack,
  Divider,
  Paper,
} from "@mui/material";
import {
  Close,
  Block,
  InfoOutlined,
  SpeakerNotes,
} from "@mui/icons-material";
import { useQuery } from "react-query";
import { getScheduleTaskDetails } from "../../api/services/scheduling";
import Loader from "../Loader/Loader";
import ExecutionDetailsCard from "../ScheduleJob/TaskDetails/ExecutionDetails";
import MedicationCards from "../ScheduleJob/TaskDetails/MedicinesDetails";
import CompletedNotesCard from "../ScheduleJob/TaskDetails/CompletedNotes";
import ChecklistCard from "../ScheduleJob/TaskDetails/ChecklistDetails";
import { formatTimeRangeToAmPm } from "../../base/FormatTime";
import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from "../../utils/time";
import { QK } from "../../base/qk";

const getStatusColor = (status) => {
  const map = {
    Pending: "warning",
    "Not Started": "info",
    "In Progress": "primary",
    "Partially Completed": "secondary",
    Completed: "success",
    Missed: "error",
    Cancelled: "error",
    Late: "secondary",
  };
  return map[status] || "default";
};

const TaskDetailsModal = ({ show, handleClose, id }) => {
  const { data: taskDetails, isLoading } = useQuery(
    [QK.taskDetails, id],
    () => getScheduleTaskDetails(id),
    { enabled: !!id }
  );

  if (isLoading) return <Loader />;

  const task = taskDetails || {};
  const isCancelled = task.status === "Cancelled";

  const formatDate = (date) =>
    date ? formatDateUtil(date) : "N/A";
  const formatDateTime = (date) =>
    date ? formatDateTimeUtil(date) : "N/A";

  return (
    <Modal open={show} onClose={handleClose}>
      <Paper
        sx={{
          width: 720,
          maxHeight: "90vh",
          overflowY: "auto",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: 2.5,
          p: 0,
        }}
      >
        <Box sx={{ p: 2.5, pb: 0 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                color="#0F172A"
                fontSize="1.05rem"
              >
                {task.visit_name || "Visit Details"}
              </Typography>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ mt: 0.5 }}
              >
                <Chip
                  label={task.status}
                  color={getStatusColor(task.status)}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    height: 20,
                    fontSize: "0.68rem",
                  }}
                />
                <Typography variant="caption" color="#94A3B8" fontSize="0.7rem">
                  ID: {task.id}
                </Typography>
              </Stack>
            </Box>
            <IconButton
              onClick={handleClose}
              size="small"
              sx={{ color: "#94A3B8" }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            gap={1.5}
            sx={{
              mt: 1.5,
              p: 1.5,
              bgcolor: "#F8FAFC",
              borderRadius: 2,
            }}
          >
            {task.client_profile_photo ? (
              <Avatar
                src={task.client_profile_photo}
                alt={task.client_name}
                sx={{ width: 40, height: 40 }}
              >
                {task.client_name?.charAt(0) || "C"}
              </Avatar>
            ) : (
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "#EFF8FF",
                  color: "#1570EF",
                  fontSize: "0.95rem",
                  fontWeight: 700,
                }}
              >
                {task.client_name?.charAt(0) || "C"}
              </Avatar>
            )}
            <Box>
              <Typography
                variant="body2"
                fontWeight={600}
                color="#0F172A"
                fontSize="0.85rem"
              >
                {task.client_name}
              </Typography>
              <Typography
                variant="caption"
                color="#64748B"
                fontSize="0.72rem"
              >
                DOB: {formatDate(task.client_dob)}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 2,
              display: "flex",
              flexWrap: "wrap",
              gap: 1.25,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                p: 1.25,
                px: 1.5,
                flex: 1,
                minWidth: 120,
                bgcolor: "#F8FAFC",
                borderRadius: 1.5,
              }}
            >
              <Typography
                variant="caption"
                color="#94A3B8"
                fontWeight={500}
                fontSize="0.68rem"
              >
                Date
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color="#0F172A"
                fontSize="0.82rem"
              >
                {formatDate(task.build_for)}
              </Typography>
              <Typography variant="caption" color="#94A3B8" fontSize="0.68rem">
                {task.weekly_day}
              </Typography>
            </Paper>
            <Paper
              variant="outlined"
              sx={{
                p: 1.25,
                px: 1.5,
                flex: 1,
                minWidth: 120,
                bgcolor: "#F8FAFC",
                borderRadius: 1.5,
              }}
            >
              <Typography
                variant="caption"
                color="#94A3B8"
                fontWeight={500}
                fontSize="0.68rem"
              >
                Scheduled
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color="#0F172A"
                fontSize="0.82rem"
              >
                {formatTimeRangeToAmPm(task.scheduled_start, task.scheduled_end)}
              </Typography>
              {task.rescheduled_start && (
                <Typography
                  variant="caption"
                  color="#D97706"
                  fontSize="0.68rem"
                >
                  Rescheduled:{" "}
                  {formatTimeRangeToAmPm(
                    task.rescheduled_start,
                    task.rescheduled_end
                  )}
                </Typography>
              )}
            </Paper>
            <Paper
              variant="outlined"
              sx={{
                p: 1.25,
                px: 1.5,
                flex: 0.5,
                minWidth: 80,
                bgcolor: "#F8FAFC",
                borderRadius: 1.5,
              }}
            >
              <Typography
                variant="caption"
                color="#94A3B8"
                fontWeight={500}
                fontSize="0.68rem"
              >
                Carers
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                color="#0F172A"
                fontSize="0.82rem"
              >
                {task.carers_required}
              </Typography>
            </Paper>
          </Box>
        </Box>

        {isCancelled && (
          <Box
            sx={{
              m: 2.5,
              p: 2,
              borderRadius: 2,
              bgcolor: "#FFF5F5",
              border: "1px solid #FECACA",
            }}
          >
            <Box display="flex" alignItems="center" gap={0.75} mb={1.5}>
              <Block sx={{ color: "#DC2626", fontSize: 20 }} />
              <Typography
                variant="body2"
                fontWeight={700}
                color="#DC2626"
                fontSize="0.85rem"
              >
                Cancelled
              </Typography>
            </Box>
            <Stack spacing={1.25}>
              <Box display="flex" gap={1.25}>
                <InfoOutlined sx={{ color: "#64748B", fontSize: 18 }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="#64748B"
                    fontWeight={600}
                    fontSize="0.68rem"
                  >
                    Reason
                  </Typography>
                  <Typography variant="body2" fontWeight={500} fontSize="0.82rem">
                    {task.cancelled_reason || "Not specified"}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" gap={1.25}>
                <SpeakerNotes sx={{ color: "#64748B", fontSize: 18 }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="#64748B"
                    fontWeight={600}
                    fontSize="0.68rem"
                  >
                    Notes
                  </Typography>
                  <Typography variant="body2" fontSize="0.82rem">
                    {task.cancelled_notes || "No additional notes"}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex" gap={1.25}>
                <Chip
                  label={task.cancelled_charge ? "Chargeable" : "No Charge"}
                  size="small"
                  sx={{ height: 20, fontSize: "0.68rem" }}
                />
                <Chip
                  label={task.pay_career ? "Pay Carer" : "No Pay"}
                  size="small"
                  sx={{ height: 20, fontSize: "0.68rem" }}
                />
              </Box>
            </Stack>
          </Box>
        )}

        <Box sx={{ px: 2.5 }}>
          <Divider sx={{ my: 1.75 }} />
          <ExecutionDetailsCard
            executionDetails={task.execution_details}
            scheduledStart={task.scheduled_start}
            scheduledEnd={task.scheduled_end}
            rescheduledStart={task.rescheduled_start}
            rescheduledEnd={task.rescheduled_end}
          />
          <MedicationCards medicines={task.medicines} />
          <CompletedNotesCard completedNotes={task.completed_notes} />
          <ChecklistCard checklists={task.checklists} />
        </Box>

        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderTop: "1px solid #E2E8F0",
            bgcolor: "#FAFBFC",
            borderRadius: "0 0 10px 10px",
          }}
        >
          <Typography variant="caption" color="#94A3B8" fontSize="0.68rem">
            Last updated: {formatDateTime(task.updated_at)}
          </Typography>
          <Button
            variant="contained"
            onClick={handleClose}
            size="small"
            sx={{
              textTransform: "none",
              borderRadius: 1.5,
              px: 3,
              py: 0.7,
              fontSize: "0.78rem",
              fontWeight: 600,
            }}
          >
            Close
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default TaskDetailsModal;